import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { ArrowUpRight, ArrowDown, Pause, Play } from 'lucide-react';
import SEO from '../common/SEO';
import ScrollFilm from '../home/ScrollFilm';
import { structuredDataTemplates } from '../../utils/seo-config';
import styles from './StudioPage.module.css';

export { styles };
export function useStudioCopy(page) {
  const { t, i18n } = useTranslation('studio');
  const prefix = i18n.language?.startsWith('es') ? '/es' : '';
  return { t, prefix, copy: t(`${page}Page`, { returnObjects: true }) };
}
export function PageSEO({ page, extra = [] }) {
  const { copy, prefix } = useStudioCopy(page);
  const url = `https://jamaklab.com${prefix}/${page}`;
  return <SEO title={copy.metaTitle} description={copy.metaDescription} url={url}
    structuredData={[structuredDataTemplates.breadcrumb([{ name: 'JAMAK', url: `https://jamaklab.com${prefix}/` }, { name: copy.metaTitle.split(' — ')[0], url }]), ...extra]} />;
}
export function PageHero({ page, children, className = '' }) {
  const { copy, t, prefix } = useStudioCopy(page);
  return <header className={`${styles.hero} ${className}`}>
    <h1>{copy.lines.map(line => <span key={line}>{line}</span>)}</h1>
    <div className={styles.heroBottom}><p>{copy.intro}</p><Link to={`${prefix}/quote`} className={styles.textLink}>{t('interior.contact')}<ArrowUpRight size={20} /></Link></div>
    {children}
  </header>;
}
export function FilmBreak({ page, film, light = false, className = '' }) {
  const { copy, t } = useStudioCopy(page);
  const ref = useRef(null);
  const prefersReduced = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const reduced = Boolean(prefersReduced || paused);
  return <section ref={ref} className={`${styles.filmRange} ${light ? styles.lightFilm : ''} ${reduced ? styles.stillFilm : ''} ${className}`} aria-label={copy.filmTitle}>
    <div className={styles.filmSticky}>
      <div className={styles.filmMedia}><ScrollFilm basePath={`/films/r01-${film}`} frameCount={48} poster={`/images/studio/r01-${film}.webp`} alt={copy.filmAlt} rangeRef={ref} reducedMotion={reduced} background={light ? '#e9e8e0' : '#080b0c'} /></div>
      <div className={styles.filmTop}><span>{t('interior.artLabel')}</span><button type="button" aria-pressed={!reduced} aria-label={t('interior.motionLabel')} disabled={Boolean(prefersReduced)} onClick={() => setPaused(v => !v)}>{reduced ? <Play size={12} /> : <Pause size={12} />}{t(reduced ? 'interior.motionOff' : 'interior.motionOn')}</button></div>
      <div className={styles.filmCaption}><h2>{copy.filmTitle}</h2><p>{copy.filmBody}</p></div>
      {!reduced && <span className={styles.filmHint}><ArrowDown size={14} />{t('interior.scroll')}</span>}
    </div>
  </section>;
}
export function PageClose({ page }) {
  const { copy, t, prefix } = useStudioCopy(page);
  return <section className={styles.close}><div><h2>{copy.cta}</h2><p>{copy.ctaBody}</p></div><Link to={`${prefix}/quote`} className={styles.closeLink}>{t('interior.contact')}<ArrowUpRight strokeWidth={1} /></Link></section>;
}
export function Method() {
  const { t } = useTranslation('studio');
  const steps = t('interior.processSteps', { returnObjects: true });
  return <section className={styles.method}><div className={styles.sectionHead}><h2>{t('interior.process')}</h2><p>{t('interior.processIntro')}</p></div><ol>{steps.map(step => <li key={step.title}><h3>{step.title}</h3><p>{step.body}</p></li>)}</ol></section>;
}
